import React, { createContext, useContext, useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Role definitions
export const ROLES = {
  AGENCY_DIRECTOR: 'agency_director',
  CLIENT_SUCCESS_MANAGER: 'client_success_manager',
  MARKETING_SPECIALIST: 'marketing_specialist',
  OPERATIONS_ASSISTANT: 'operations_assistant'
};

// Role display names
export const ROLE_NAMES = {
  [ROLES.AGENCY_DIRECTOR]: 'Agency Director',
  [ROLES.CLIENT_SUCCESS_MANAGER]: 'Client Success Manager',
  [ROLES.MARKETING_SPECIALIST]: 'Marketing Specialist',
  [ROLES.OPERATIONS_ASSISTANT]: 'Operations Assistant'
};

// Role badges
export const ROLE_BADGES = {
  [ROLES.AGENCY_DIRECTOR]: 'AD',
  [ROLES.CLIENT_SUCCESS_MANAGER]: 'CSM',
  [ROLES.MARKETING_SPECIALIST]: 'MS',
  [ROLES.OPERATIONS_ASSISTANT]: 'OA'
};

// Permission definitions
export const PERMISSIONS = {
  // System & Settings
  MANAGE_SYSTEM_SETTINGS: 'manage_system_settings',
  MANAGE_STAFF: 'manage_staff',
  MANAGE_ROLES: 'manage_roles',
  
  // Leads
  VIEW_ALL_LEADS: 'view_all_leads',
  EDIT_LEADS: 'edit_leads',
  CONVERT_LEADS: 'convert_leads',
  DELETE_LEADS: 'delete_leads',
  
  // Clients
  VIEW_ALL_CLIENTS: 'view_all_clients',
  VIEW_ASSIGNED_CLIENTS: 'view_assigned_clients',
  EDIT_CLIENTS: 'edit_clients',
  ASSIGN_CLIENTS: 'assign_clients',
  DELETE_CLIENTS: 'delete_clients',
  
  // Billing & Contracts
  VIEW_BILLING: 'view_billing',
  MANAGE_BILLING: 'manage_billing',
  OVERRIDE_PAYMENTS: 'override_payments',
  VIEW_CONTRACTS: 'view_contracts',
  MANAGE_CONTRACTS: 'manage_contracts',
  OVERRIDE_CONTRACT_STATUS: 'override_contract_status',
  
  // Client Operations
  MANAGE_ONBOARDING_TASKS: 'manage_onboarding_tasks',
  MANAGE_ASSETS: 'manage_assets',
  MANAGE_SUPPORT_TICKETS: 'manage_support_tickets',
  
  // Messaging
  MESSAGE_ALL_CLIENTS: 'message_all_clients',
  MESSAGE_ASSIGNED_CLIENTS: 'message_assigned_clients',
  
  // Notes & Internal
  ADD_NOTES: 'add_notes',
  EDIT_NOTES: 'edit_notes',
  VIEW_AUDIT_LOGS: 'view_audit_logs'
};

const RoleContext = createContext();

export const RoleProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [assignedClients, setAssignedClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      // Try unified endpoint that works for both admin and staff
      const response = await fetch(`${API}/api/user/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
        setUserPermissions(data.permissions || []);
        setAssignedClients(data.assigned_clients || []);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permission) => {
    return userPermissions.includes(permission);
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(p => userPermissions.includes(p));
  };

  const hasAllPermissions = (permissions) => {
    return permissions.every(p => userPermissions.includes(p));
  };

  const canAccessClient = (clientId) => {
    // Agency Director and CSM can access all clients
    if (hasPermission(PERMISSIONS.VIEW_ALL_CLIENTS)) {
      return true;
    }
    
    // Others can only access assigned clients
    if (hasPermission(PERMISSIONS.VIEW_ASSIGNED_CLIENTS)) {
      return assignedClients.includes(clientId);
    }
    
    return false;
  };

  const canEditClient = (clientId) => {
    return hasPermission(PERMISSIONS.EDIT_CLIENTS) && canAccessClient(clientId);
  };

  const isAgencyDirector = () => userRole === ROLES.AGENCY_DIRECTOR;
  const isCSM = () => userRole === ROLES.CLIENT_SUCCESS_MANAGER;
  const isMS = () => userRole === ROLES.MARKETING_SPECIALIST;
  const isOA = () => userRole === ROLES.OPERATIONS_ASSISTANT;

  const getRoleDisplayName = () => ROLE_NAMES[userRole] || 'Team Member';
  const getRoleBadge = () => ROLE_BADGES[userRole] || '';

  return (
    <RoleContext.Provider value={{
      userRole,
      userPermissions,
      assignedClients,
      loading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessClient,
      canEditClient,
      isAgencyDirector,
      isCSM,
      isMS,
      isOA,
      getRoleDisplayName,
      getRoleBadge,
      refreshUserInfo: fetchUserInfo
    }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

// Higher-order component for route protection
export const withRoleCheck = (Component, requiredPermission) => {
  return (props) => {
    const { hasPermission, loading } = useRole();

    if (loading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-gray-600">Loading...</div>
        </div>
      );
    }

    if (!hasPermission(requiredPermission)) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};
